import type { PayrollTableExportRequest } from '../../../../api/Payroll/payrollReports';
import { reportExportLabels } from '../../../../components/reports/reportExportRequest';
import {
  orderedColumns,
  type ColumnFamily,
  type ColumnKey,
  type ExportGroupBy,
  type ExportLayout,
  type ExportOptions,
  type ExportPreset,
  type ExportSortBy,
} from './exportOptions';

type T = (key: string, options?: Record<string, unknown>) => string;

/**
 * CO-995 — every enum the panel offers, spelled out as a literal `t()` call. Enums are never passed as
 * keys: the i18n audit can only see a literal, so a composed key would ship untranslated without a sound.
 */

export function columnLabel(key: ColumnKey, t: T): string {
  switch (key) {
    case 'name': return t('Employee Name');
    case 'staffNumber': return t('profile.staffNo');
    case 'department': return t('Department');
    case 'position': return t('Position');
    case 'country': return t('Country');
    case 'fixedPay': return t('Fixed pay');
    case 'variablePay': return t('Variable Pay');
    case 'adjustments': return t('Adjustments');
    case 'overtimePay': return t('payrollReports.overtimePay');
    case 'deductions': return t('payrollExport.column.deductions');
    case 'netPay': return t('Total Net Pay');
    case 'regularOvertimeHours': return t('payrollExport.column.regularOvertimeHours');
    case 'premiumOvertimeHours': return t('payrollExport.column.premiumOvertimeHours');
    case 'paymentStatus': return t('payrollExport.column.paymentStatus');
    case 'bankName': return t('payrollExport.column.bankName');
    case 'iban': return t('payrollExport.column.iban');
    case 'labourCard': return t('labourCardNumber');
  }
}

export function familyLabel(family: ColumnFamily, t: T): string {
  switch (family) {
    case 'employee': return t('payrollExport.family.employee');
    case 'pay': return t('payrollExport.family.pay');
    case 'overtimeHours': return t('payrollExport.family.overtimeHours');
    case 'payment': return t('payrollExport.family.payment');
  }
}

export function presetLabel(preset: ExportPreset, t: T): string {
  switch (preset) {
    case 'paySummary': return t('payrollExport.preset.paySummary');
    case 'fullBreakdown': return t('payrollExport.preset.fullBreakdown');
    case 'bankSheet': return t('payrollExport.preset.bankSheet');
  }
}

export function presetHint(preset: ExportPreset | null, t: T): string {
  switch (preset) {
    case 'paySummary': return t('payrollExport.preset.paySummaryHint');
    case 'fullBreakdown': return t('payrollExport.preset.fullBreakdownHint');
    case 'bankSheet': return t('payrollExport.preset.bankSheetHint');
    default: return t('payrollExport.preset.customHint');
  }
}

export function layoutLabel(layout: ExportLayout, t: T): string {
  switch (layout) {
    case 'register': return t('payrollExport.layout.register');
    case 'summaryAndRegister': return t('payrollExport.layout.summaryAndRegister');
    case 'oneSheetPerGroup': return t('payrollExport.layout.oneSheetPerGroup');
  }
}

export function layoutHint(layout: ExportLayout, t: T): string {
  switch (layout) {
    case 'register': return t('payrollExport.layout.registerHint');
    case 'summaryAndRegister': return t('payrollExport.layout.summaryAndRegisterHint');
    case 'oneSheetPerGroup': return t('payrollExport.layout.oneSheetPerGroupHint');
  }
}

export function groupByLabel(groupBy: ExportGroupBy, t: T): string {
  switch (groupBy) {
    case 'none': return t('payrollExport.groupBy.none');
    case 'department': return t('Department');
    case 'position': return t('Position');
    case 'country': return t('Country');
    case 'paymentStatus': return t('payrollExport.column.paymentStatus');
  }
}

export function sortByLabel(sortBy: ExportSortBy, t: T): string {
  switch (sortBy) {
    case 'name': return t('Employee Name');
    case 'staffNumber': return t('profile.staffNo');
    case 'netPayHighToLow': return t('payrollExport.sort.netPayHighToLow');
  }
}

export interface BuildRequestParams {
  options: ExportOptions;
  payslipIds: number[];
  year: number;
  month: number;
  currencyId: number;
  periodText: string;
  culture: string;
  rtl: boolean;
  t: T;
}

/** The client owns the words and the server owns the figures: no row data travels in this request. */
export function buildExportRequest({
  options, payslipIds, year, month, currencyId, periodText, culture, rtl, t,
}: BuildRequestParams): PayrollTableExportRequest {
  return {
    format: 'xlsx',
    culture,
    rtl,
    title: t('payrollExport.workbook.title'),
    columns: orderedColumns(options.columns).map((key) => ({ key, header: columnLabel(key, t) })),
    labels: { ...reportExportLabels(t), period: periodText, scope: '' },
    input: {
      year,
      month,
      currencyId,
      payslipIds,
      layout: options.layout,
      groupBy: options.groupBy,
      sortBy: options.sortBy,
      showSubtotals: options.showSubtotals,
      text: {
        period: t('reports.period'),
        currency: t('payrollExport.workbook.currency'),
        employees: t('payrollExport.workbook.employees'),
        // {n} stays for the server, which fills in each count: the word order is the language's own.
        employeeCountOne: t('payrollExport.workbook.employeeCountOne', { n: '{n}' }),
        employeeCountMany: t('payrollExport.workbook.employeeCountMany', { n: '{n}' }),
        paid: t('payroll.status.paid'),
        notPaid: t('Not paid yet'),
        noDepartment: t('payrollExport.workbook.noDepartment'),
        noPosition: t('payrollExport.workbook.noPosition'),
        noCountry: t('payrollExport.workbook.noCountry'),
        allEmployees: t('payrollExport.workbook.allEmployees'),
        // The summary's first column reads as what the rows are, not as the word "Group".
        groupHeader: options.groupBy === 'none' ? t('payrollExport.workbook.group') : groupByLabel(options.groupBy, t),
        shareOfNetPay: t('payrollExport.workbook.shareOfNetPay'),
        registerSheet: t('payrollExport.workbook.registerSheet'),
        summarySheet: t('payrollExport.workbook.summarySheet'),
        indexSheet: t('payrollExport.workbook.indexSheet'),
        backToIndex: t('payrollExport.workbook.backToIndex'),
        note: t('payrollExport.workbook.note'),
        notConverted: t('payrollExport.workbook.notConverted'),
        notConvertedHeading: t('payrollExport.workbook.notConvertedHeading'),
        unconvertedRowNote: t('payrollExport.workbook.unconvertedRowNote'),
        unconvertedFootnote: t('payrollExport.workbook.unconvertedFootnote'),
      },
    },
  };
}
